/**
 * avatar.ts — the SINGLE client-side chokepoint that turns a stored avatar reference into a URL fit
 * for an <img src>. Shared by the owner's own profile (Profile.tsx) and a colleague's public
 * profile (UserProfile.tsx) so the XSS/SSRF-critical validation lives in exactly ONE place and can
 * never drift between the two pages.
 *
 * SECURITY — this is defence-in-depth on top of the server + DB validation. The presentation avatar
 * (UR-F30) is either:
 *   - a STOCK pick: the stored ref is an opaque server token 'stock:<id>'. We NEVER emit that token;
 *     we map <id> through the STOCK_AVATARS allowlist and emit ONLY the fixed first-party bundled
 *     asset URL. An unknown id falls through to the directory photo.
 *   - an UPLOAD: the stored ref is a data: URI. We emit it ONLY when it matches AVATAR_UPLOAD_URI_RE
 *     (an allowlisted image MIME + strict base64). A `javascript:` / `http(s):` / `data:text/html` /
 *     script-bearing SVG ref fails the test and falls through — so a hostile ref can never smuggle a
 *     URL into `src` (no XSS, no SSRF, no tracking pixel).
 *   - anything else (null, a mis-shaped ref, an unknown kind): falls through to the directory photo,
 *     and null from there means the caller renders a monogram.
 *
 * A Map (not a plain object) closes the prototype-key hole for stock ids (`__proto__`, `constructor`).
 */

/** The minimal avatar shape both profile pages carry: a kind + an opaque/validated reference. */
export interface AvatarRef {
  avatarKind: string | null;
  avatarBlobRef: string | null;
}

/**
 * The stock avatars a person can pick. KEYS are the stable stock ids the backend allowlists
 * (routes/me.ts STOCK_AVATARS); values are the bundled asset URLs served from public/avatars/. The
 * backend persists the opaque token 'stock:<id>' and never a URL — so this map is the ONLY place a
 * token turns back into an image. Keep it in step with the server allowlist.
 */
export const STOCK_AVATARS = new Map<string, { url: string; label: string }>([
  ['classic', { url: '/avatars/classic.webp', label: 'Classic' }],
]);

/**
 * The ONLY data: URI shapes resolveAvatar will emit for an uploaded avatar: a base64-encoded image
 * of an allowlisted MIME (webp/png/jpeg). Mirrors the server's AVATAR_DATA_URI_RE. A stored
 * `javascript:`, `http(s):`, `data:text/html`, or `data:image/svg+xml` ref can therefore NEVER
 * reach an <img src> here — this regex is the last render-path chokepoint.
 */
export const AVATAR_UPLOAD_URI_RE = /^data:image\/(?:webp|png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/;

/**
 * Resolve the avatar image URL to display, honouring the presentation-only override (UR-F30): a
 * valid override (stock OR uploaded) wins over the directory photo, which wins over nothing (→ the
 * caller renders a monogram when this returns null). See the module header for the security model.
 */
export function resolveAvatar(ref: AvatarRef | null, directoryPhoto: string | null): string | null {
  const blobRef = ref?.avatarBlobRef;
  if (ref?.avatarKind === 'stock' && typeof blobRef === 'string' && blobRef.startsWith('stock:')) {
    const id = blobRef.slice('stock:'.length);
    const hit = id ? STOCK_AVATARS.get(id) : undefined;
    if (hit) return hit.url; // an allowlisted, first-party bundled asset — the ONLY stock URL we emit
  }
  if (
    ref?.avatarKind === 'upload' &&
    typeof blobRef === 'string' &&
    AVATAR_UPLOAD_URI_RE.test(blobRef)
  ) {
    return blobRef; // a validated image data: URI — the ONLY case an avatarBlobRef reaches <img src>
  }
  return directoryPhoto; // directory photo, or null → monogram fallback in the caller
}

/** A monogram from a display name, for when there is no photo (the common case). */
export function monogram(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}
